import { UAContext } from '@quentin-sommer/react-useragent';
import moment from 'moment';
import React, { useContext, useMemo } from 'react';
import Button from '~components/button';
import { ExtensionCard } from '~components/my-bookings';
import StatusButton from '~components/navigation-panel/status-button';
import {
  useTakeoverConfirm,
  useTakeoverThroughOccupiedBooking,
  useTakeoverThroughOriginalBooking,
} from '~components/takeover/use-takeover';
import { OrgOptions, useThemeContext } from '~components/theme-provider';
import { TippyBase } from '~components/tippy';
import Typography from '~components/typography';
import {
  AmendmentDetailOption,
  AmendmentDetailStatusOption,
  BookingStatusOption,
  PaymentItemStatusOptions,
  TenancyLengthOptions,
} from '~graphql/__generated__/globalTypes';
import { SP_QueryStudentGetBookings_studentViewer_studentInfo_bookings } from '~graphql/__generated__/SP_QueryStudentGetBookings';
import { Router, useTranslation } from '~i18n';
import { IconEarly, IconExtend, IconRight, More } from '~svg-components';
import { getPropertyImageUrl } from '~utils/js-helper';
import { useIsExtensionBooking } from '~utils/use-amendment';
import { BookingStatusCanceledAndPastAndExpired } from '~utils/use-status';
import styles from './booking-card.module.scss';
import BookingCardAmendment from './booking-card-amendment';
import BookingCardConfrim from './booking-card-confirm';
import {
  BookingType,
  useGetBookingCardName,
  useGetBookingCardStatus,
  useGetBookingCardType,
} from './booking-card-helper';
import BookingCardPayment from './booking-card-payment';
import BookingCardTakeover from './booking-card-takeover';

interface IBookingCardProps {
  booking: SP_QueryStudentGetBookings_studentViewer_studentInfo_bookings;
  contact: any;
}
const BookingCard: React.FC<IBookingCardProps> = ({ booking, contact }) => {
  const uaResults = useContext(UAContext).uaResults as UAResults;
  const isMobile = uaResults.mobile;
  const { t } = useTranslation(['student_portal']);
  const { ORG } = useThemeContext();

  const {
    status,
    property,
    tenancyOption,
    latestAmendmentDetail,
    amendmentDetails,
    bookingLink,
    tenancyLengthOption,
    sourceRollingBooking,
    isRolling,
    isRolledOver,
  } = booking;

  const isRollingBooking = tenancyLengthOption === TenancyLengthOptions.ROLLING;

  const { sourceAcceptedAt, targetAcceptedAt } = bookingLink || {};
  const takeoverComfirm = useTakeoverConfirm(booking, t);

  const isExtensionBooking = useIsExtensionBooking(
    latestAmendmentDetail?.status,
    latestAmendmentDetail?.type
  );

  const bookingCardType = useGetBookingCardType(booking);
  const amendmentHasAwaitingPaymentItems = useMemo(() => {
    const bookingStatusIsTermsAccepted = booking?.targetBookings?.find(
      item => item?.status === BookingStatusOption.TERMS_ACCEPTED
    );
    return bookingStatusIsTermsAccepted?.paymentItems?.some(
      item => item?.status === PaymentItemStatusOptions.AWAITING
    );
  }, [booking]);

  const bookingCardName = useGetBookingCardName(booking);

  const bookingCardStatus = useGetBookingCardStatus(booking);

  const isTakeoverThroughOriginalBooking = useTakeoverThroughOriginalBooking(booking);
  const isTakeoverThroughOccupiedBooking = useTakeoverThroughOccupiedBooking(booking);

  const bookingIsPastOrArchived = useMemo(() => {
    if (!status) return false;
    return BookingStatusCanceledAndPastAndExpired.includes(status);
  }, [status]);

  const getExtensionBooking = useMemo(() => {
    if (isExtensionBooking) {
      return booking?.activeBookingsOnSameChain?.find(item => {
        return item?.id === latestAmendmentDetail?.targetBookingId;
      });
    }
  }, [booking, isExtensionBooking, latestAmendmentDetail]);

  const isShowExtensionStay = useMemo(() => {
    if (!latestAmendmentDetail && amendmentDetails?.length > 0) {
      const extendBooking = amendmentDetails?.find(item => {
        return (
          item?.targetBookingId === booking?.id &&
          (item?.type === AmendmentDetailOption.TENANCY_EXTEND_EARLY_CHECK_IN ||
            item?.type === AmendmentDetailOption.TENANCY_EXTEND_LATER_CHECK_OUT) &&
          (item?.status === AmendmentDetailStatusOption.CONFIRMED ||
            item?.status === AmendmentDetailStatusOption.COMPLETED)
        );
      });

      return !!extendBooking;
    }
  }, [amendmentDetails, booking, latestAmendmentDetail]);

  const isDisplayTakeoverConfirm = useMemo(() => {
    if (bookingCardType !== BookingType.TAKEOVER) return false;

    if (
      (isTakeoverThroughOriginalBooking && sourceAcceptedAt) ||
      (isTakeoverThroughOccupiedBooking && targetAcceptedAt)
    ) {
      return false;
    }

    return true;
  }, [
    bookingCardType,
    isTakeoverThroughOccupiedBooking,
    isTakeoverThroughOriginalBooking,
    sourceAcceptedAt,
    targetAcceptedAt,
  ]);

  const renderBookingTenancy = useMemo(() => {
    if (isRollingBooking && isRolling && !isRolledOver && !bookingIsPastOrArchived) {
      return (
        <Typography variant="body3">
          {t('from')} {moment(tenancyOption?.startDate).format('ddd, ll')}
        </Typography>
      );
    }
    return (
      <>
        <Typography variant="body3">
          {moment(tenancyOption?.startDate).format('ddd, ll')}
        </Typography>
        <IconRight className="booking-card__icon-right" fill="var(--color-purple_4)" />
        <Typography variant="body3">{moment(tenancyOption?.endDate).format('ddd, ll')}</Typography>
      </>
    );
  }, [bookingIsPastOrArchived, isRolledOver, isRolling, isRollingBooking, t, tenancyOption]);

  const isRollingBookingAndHasSourceRollingBooking = useMemo(() => {
    return (
      isRollingBooking &&
      (status === BookingStatusOption.BOOKING_CONFIRMED ||
        status === BookingStatusOption.BOOKING_COMPLETED) &&
      sourceRollingBooking?.id
    );
  }, [isRollingBooking, sourceRollingBooking, status]);

  return (
    <div className={styles.root}>
      <div className="booking-card__original">
        <div
          className="booking-card__property-img-wraper"
          onClick={() => {
            if (isMobile) Router.push(`/booking-details?bookingId=${booking?.id}`);
          }}
        >
          <img
            className="booking-card__property-img"
            src={getPropertyImageUrl(property?.imageName)}
            alt=""
          />
          <StatusButton
            status={bookingCardStatus.status}
            name={bookingCardStatus.name}
            className="booking-card__status-btn"
          />
          {!!isShowExtensionStay && (
            <div className="booking-card__extended-stay">
              <IconExtend
                fill="var(--color-white)"
                width="18"
                height="6"
                className="booking-card__icon-extend"
              />
              <Typography variant="body3" color="var(--color-white)">
                {t('amendment.extension.extended_stay')}
              </Typography>
            </div>
          )}
        </div>
        <div className="booking-card__content">
          <div className="booking-card__content-info">
            <Typography
              className="booking-card__content-title"
              component="div"
              variant={isMobile ? 'h8' : 'h7'}
            >
              {bookingCardName}
            </Typography>
            <div className="booking-card__content-date">
              {renderBookingTenancy}
              <Typography variant="body3">{` (${tenancyOption?.name})`}</Typography>
              {tenancyOption?.originalEndDate &&
                tenancyOption?.endDate !== tenancyOption?.originalEndDate && (
                  <TippyBase
                    style={{ width: 20, height: 20, marginLeft: 8 }}
                    content={t('tenancy_early_date', {
                      date: moment(tenancyOption?.originalEndDate).format('ddd, ll'),
                    })}
                    icon={<IconEarly width="20" height="20" />}
                  />
                )}
            </div>

            {bookingCardType === BookingType.TAKEOVER && <BookingCardTakeover booking={booking} />}

            {bookingCardType === BookingType.AMENDMENT && (
              <BookingCardAmendment booking={booking} />
            )}

            {(bookingCardType === BookingType.CONFIRM ||
              (bookingCardType === BookingType.EXTENSION &&
                status !== BookingStatusOption.RESIDENT)) &&
              !isRollingBookingAndHasSourceRollingBooking && (
                <BookingCardConfrim booking={booking} contact={contact} />
              )}

            {(bookingCardType === BookingType.PAYMENT ||
              (bookingCardType === BookingType.EXTENSION &&
                status === BookingStatusOption.RESIDENT)) && (
              <BookingCardPayment booking={booking} />
            )}
          </div>
          <div className="booking-card__href">
            <div className="booking-card__href-detail">
              <Typography
                variant="body3"
                cursor="pointer"
                textDecoration="underline"
                onClick={() => Router.push(`/booking-details?bookingId=${booking?.id}`)}
              >
                {t('booking_confirmed.view_booking_btn')}
              </Typography>
              <div className="booking-card__href-detail-more">
                <More
                  className="booking-card__href-detail-icon"
                  fill={ORG === OrgOptions.BEYOO ? 'var(--color-white)' : 'var(--color-purple_6)'}
                  width="10"
                  height="11"
                />
              </div>
            </div>

            {isDisplayTakeoverConfirm && (
              <Button fullWidth={isMobile} onClick={() => takeoverComfirm && takeoverComfirm()}>
                {t('amendment.go_confirm')}
              </Button>
            )}

            {bookingCardType === BookingType.AMENDMENT && (
              <Button
                fullWidth={isMobile}
                onClick={() => {
                  Router.push(
                    `/amendment-process?bookingId=${latestAmendmentDetail?.targetBookingId}`
                  );
                }}
              >
                {amendmentHasAwaitingPaymentItems
                  ? t('bank_transfer_details')
                  : t('amendment.go_confirm')}
              </Button>
            )}
            {bookingCardType === BookingType.CONFIRM && (
              <Button
                fullWidth={isMobile}
                onClick={() => {
                  Router.push(`/check-in?bookingId=${booking?.id}`);
                }}
              >
                {t('my_stay.view_checkin_details')}
              </Button>
            )}
            {bookingCardType === BookingType.EXTENSION &&
              booking?.status !== BookingStatusOption.RESIDENT &&
              !isMobile && (
                <Button
                  fullWidth={isMobile}
                  onClick={() => {
                    Router.push(`/check-in?bookingId=${booking?.id}`);
                  }}
                >
                  {t('my_stay.view_checkin_details')}
                </Button>
              )}
            {bookingCardType === BookingType.EXTENSION &&
              booking?.status === BookingStatusOption.RESIDENT &&
              !isMobile && (
                <Button
                  fullWidth={isMobile}
                  onClick={() => {
                    Router.push(`/my-payments?bookingId=${booking?.id}`);
                  }}
                >
                  {t('my_stay.go_to_payment')}
                </Button>
              )}
            {bookingCardType === BookingType.PAYMENT && (
              <Button
                fullWidth={isMobile}
                onClick={() => {
                  Router.push(`/my-payments?bookingId=${booking?.id}`);
                }}
              >
                {t('my_stay.go_to_payment')}
              </Button>
            )}
          </div>
        </div>
      </div>
      {bookingCardType === BookingType.EXTENSION && getExtensionBooking && (
        <ExtensionCard booking={getExtensionBooking} />
      )}
      {bookingCardType === BookingType.EXTENSION &&
        isMobile &&
        booking?.status !== BookingStatusOption.RESIDENT && (
          <Button
            fullWidth={isMobile}
            onClick={() => {
              Router.push(`/check-in?bookingId=${booking?.id}`);
            }}
          >
            {t('my_stay.view_checkin_details')}
          </Button>
        )}
      {bookingCardType === BookingType.EXTENSION &&
        isMobile &&
        booking?.status === BookingStatusOption.RESIDENT && (
          <Button
            fullWidth={isMobile}
            onClick={() => {
              Router.push(`/my-payments?bookingId=${booking?.id}`);
            }}
          >
            {t('my_stay.go_to_payment')}
          </Button>
        )}
      <style jsx>
        {`
          .booking-card {
            :global(&__icon-extend) {
              margin-right: 4px;
            }
            &__extended-stay {
              position: absolute;
              top: 12px;
              left: 12px;
              height: 28px;
              padding: 0 16px;
              background: rgba(42, 42, 42, 0.75);
              border-radius: 14px;
              display: flex;
              align-items: center;
            }
            &__original {
              display: flex;
              align-items: center;
            }
            &__property-img-wraper {
              width: 304px;
              height: 204px;
              position: relative;
            }
            &__property-img {
              width: 100%;
              height: 100%;
            }
            &__content {
              flex: 1;
              padding: 24px 28px 0 20px;
              height: 204px;
              border-left: none;
              display: flex;
              border-bottom: ${bookingCardType === BookingType.EXTENSION
                ? `1px solid var(--color-purple_2)`
                : 'none'};
            }
            :global(&__content-title) {
              height: 24px;
              width: 500px;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
            &__content-info {
              flex: 1;
            }
            &__content-date {
              display: flex;
              align-items: center;
              flex-wrap: wrap;
              margin-top: 4px;
            }
            :global(&__content-date-status) {
              margin-left: 4px;
            }
            &__href {
              width: 238px;
              padding-left: 16px;
              height: 156px;
              border-left: 1px solid var(--color-purple_2);
              display: flex;
              flex-direction: column;
              justify-content: space-between;
              align-items: flex-end;
            }
            &__href-detail {
              display: flex;
              align-items: center;
            }
            &__href-detail-more {
              width: 20px;
              height: 20px;
              flex-shrink: 0;
              border-radius: 10px;
              margin-left: 8px;
              background: ${ORG === OrgOptions.BEYOO
                ? 'var(--color-green_6)'
                : 'var(--color-yellow_6)'};
              display: flex;
              align-items: center;
              justify-content: center;
            }
            :global(&__href-detail-icon) {
              margin-left: 2px;
              transform: rotate(-90deg);
            }
            :global(&__icon-right) {
              width: 12px;
              height: 10px;
              margin-left: 8px;
              margin-right: 8px;
            }
            :global(&__status-btn) {
              position: absolute;
              bottom: 12px;
              left: 12px;
            }
          }
          @media screen and (max-width: 768px) {
            .booking-card {
              &__original {
                display: block;
              }
              &__content {
                flex: 1;
                padding: 12px 0 0 0;
                height: auto;
                display: block;
                border-bottom: none;
              }
              :global(&__content-title) {
                overflow: visible;
                word-break: break-word;
                text-overflow: unset;
                white-space: normal;
                height: auto;
                width: 100%;
              }
              &__href {
                width: 100%;
                height: auto;
                border-left: none;
                display: block;
                margin-top: 20px;
              }
              &__href-detail {
                display: none;
              }
              &__content-date {
                margin-top: 8px;
              }
              &__property-img-wraper {
                width: 100%;
                height: 211px;
                position: relative;
              }
              &__property-img {
                border-radius: 14px;
              }
            }
          }
        `}
      </style>
    </div>
  );
};

export default BookingCard;
